import { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface MathTextProps {
  text: string;
  className?: string;
}

/**
 * Renders text with inline LaTeX math expressions.
 * LaTeX is delimited by $...$ for inline math.
 *
 * Example: "Solve $x^2 + 3 = 12$" renders with proper math formatting.
 */
export function MathText({ text, className }: MathTextProps) {
  const rendered = useMemo(() => {
    if (!text) return null;

    // Split on $...$ patterns, keeping the delimiters
    const parts = text.split(/(\$[^$]+\$)/g);

    return parts.map((part, index) => {
      // Check if this part is a LaTeX expression (wrapped in $...$)
      if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
        const latex = part.slice(1, -1); // Remove $ delimiters
        try {
          const html = katex.renderToString(latex, {
            throwOnError: false,
            displayMode: false,
            strict: false,
            trust: true,
          });
          return (
            <span
              key={index}
              className="katex-wrapper"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          );
        } catch (e) {
          // If KaTeX fails, show just the math content without delimiters
          console.warn('KaTeX render error for:', latex, e);
          return <span key={index}>{latex}</span>;
        }
      }
      // Regular text - return null for empty strings
      if (!part) return null;
      return <span key={index}>{part}</span>;
    });
  }, [text]);

  return <span className={className}>{rendered}</span>;
}

/**
 * Check if text contains LaTeX math expressions
 */
export function containsMath(text: string): boolean {
  return /\$[^$]+\$/.test(text);
}
